import React, { useMemo } from "react";
import { Menu } from "antd";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  CameraOutlined,
  CarryOutOutlined,
  ClusterOutlined,
  CloudUploadOutlined,
  CompassOutlined,
  DashboardOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  GlobalOutlined,
  GroupOutlined,
  IdcardOutlined,
  LogoutOutlined,
  NodeIndexOutlined,
  OrderedListOutlined,
  PushpinOutlined,
  SolutionOutlined,
  TeamOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import { SIDE_NAV } from "../config/lang";
import { logout } from "../util/redux/actions";
import { translate } from "../util/util";

/**
 * Navigation model. `icon` names come from the verified mapping in
 * docs/design/spec/02-components.md#icons — 20 entries, all distinct, so no two nav rows share
 * a glyph. `role` gates visibility; "all" means every signed-in role.
 */
const MENU = [
  { type: "section", key: "sec_overview", label: { uk: "Огляд", en: "Overview", hy: "Ակնարկ", ka: "მიმოხილვა" }, role: ["all"] },
  { name: "overview", path: "/overview", Icon: DashboardOutlined, role: ["all"] },

  { type: "section", key: "sec_main", label: { uk: "Основне", en: "Main", hy: "Հիմնական", ka: "მთავარი" }, role: ["all"] },
  { name: "photo_report", path: "/photo-review", Icon: CameraOutlined, role: ["all"] },
  { name: "visit", path: "/visit", Icon: EnvironmentOutlined, role: ["all"] },
  { name: "tracking", path: "/tracking", Icon: NodeIndexOutlined, role: ["all"] },
  { name: "all_reports", path: "/bi-report", Icon: BarChartOutlined, role: ["all"] },

  { type: "section", key: "sec_management", label: { uk: "Управління", en: "Management", hy: "Կառավարում", ka: "მართვა" }, role: ["all"] },
  { name: "tasks", path: "/task", Icon: CarryOutOutlined, role: ["all"] },
  { name: "answers", path: "/answers", Icon: SolutionOutlined, role: ["all"] },
  /* Two rows, two routes. They used to be `/tasks-import/3` and `/tasks-import` — one route with
     the tab index in the path — which `activeKey` resolved correctly only by the accident that
     `/tasks-import/3` is the longer string. */
  { name: "my_uploads", path: "/uploads", Icon: UploadOutlined, role: ["supervisor_manager"] },
  { name: "task_uploading", path: "/import", Icon: CloudUploadOutlined, role: ["supervisor_manager"] },

  {
    type: "section",
    key: "sec_personnel",
    label: { uk: "Управління персоналом", en: "Personnel management", hy: "Անձնակազմի կառավարում", ka: "პერსონალის მართვა" },
    role: ["all"],
  },
  { name: "merchandiser", path: "/merchandiser", Icon: IdcardOutlined, role: ["all"] },
  { name: "supervisors", path: "/supervisors", Icon: TeamOutlined, role: ["supervisor_manager"] },

  { type: "section", key: "sec_data", label: { uk: "Дані", en: "Data", hy: "Տվյալներ", ka: "მონაცემები" }, role: ["supervisor_manager", "supervisor"] },
  { name: "documents", path: "/document", Icon: FileTextOutlined, role: ["all"] },

  {
    type: "section",
    key: "sec_surveys",
    label: { uk: "Опитування", en: "Surveys", hy: "Հարցումներ", ka: "გამოკითხვები" },
    role: ["supervisor_manager", "supervisor"],
  },
  { name: "pt_surveys", path: "/surveys", Icon: OrderedListOutlined, role: ["supervisor_manager", "supervisor"] },

  { type: "section", key: "sec_points", label: { uk: "Точки", en: "Points", hy: "Կետեր", ka: "წერტილები" }, role: ["supervisor_manager", "supervisor"] },
  { name: "pt_points", path: "/points", Icon: PushpinOutlined, role: ["supervisor_manager", "supervisor"] },
  { name: "pt_networks", path: "/networks", Icon: ClusterOutlined, role: ["supervisor_manager", "supervisor"] },
  { name: "pt_categories", path: "/categories", Icon: AppstoreOutlined, role: ["supervisor_manager", "supervisor"] },
  { name: "pt_countries", path: "/countries", Icon: GlobalOutlined, role: ["supervisor_manager", "supervisor"] },
  { name: "pt_counties", path: "/counties", Icon: CompassOutlined, role: ["supervisor_manager", "supervisor"] },
  { name: "pt_localities", path: "/localities", Icon: ApartmentOutlined, role: ["supervisor_manager", "supervisor"] },
  { name: "pt_point_groups", path: "/point_groups", Icon: GroupOutlined, role: ["supervisor_manager", "supervisor"] },
];

const hasRole = (itemRole, userRole) =>
  Array.isArray(itemRole) ? itemRole.includes(userRole) || itemRole.includes("all") : itemRole === userRole || itemRole === "all";

/** Longest matching path wins, so /points/12 highlights Points rather than nothing. */
function activeKey(pathname, items) {
  const paths = items.filter(i => !i.type).map(i => i.path);
  const matches = paths.filter(p => pathname === p || pathname.startsWith(p + "/"));
  return matches.sort((a, b) => b.length - a.length)[0];
}

export default function AppNav({ onNavigate }) {
  const me = useSelector(state => state.me);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const lang = me?.language;

  const items = useMemo(() => {
    const visible = MENU.filter(i => hasRole(i.role, me?.role));
    const out = [];
    for (const item of visible) {
      if (item.type === "section") {
        out.push({
          key: item.key,
          type: "group",
          label: item.label[lang] || item.label.en || item.label.uk,
        });
        continue;
      }
      const label = translate(SIDE_NAV, item.name, lang) || item.name;
      out.push({
        key: item.path,
        icon: <item.Icon />,
        label: (
          <Link to={item.path} onClick={onNavigate}>
            {label}
          </Link>
        ),
      });
    }
    return out;
  }, [me?.role, lang, onNavigate]);

  const logoutLabel = translate(SIDE_NAV, "logout", lang) || "Logout";

  return (
    <>
      <Menu
        className="shell-nav"
        theme="dark"
        mode="inline"
        /* Groups are not selectable, so a group key can never be "selected" here. */
        selectedKeys={[activeKey(pathname, MENU)].filter(Boolean)}
        items={items}
      />
      <div className="shell-nav__foot">
        <button className="shell-logout" type="button" onClick={() => dispatch(logout())}>
          <LogoutOutlined />
          <span>{logoutLabel}</span>
        </button>
      </div>
    </>
  );
}
